import * as React from 'react';
import { toast } from 'react-toastify';
import GooglePlacesDropdown from './GooglePlaces';
import type { CustomerList, CustomerContactList } from './models/CustomerInterfaces';
import type { GenericList } from './models/JobSharedInterfaces';
import type { JobDetailsNew, AddContact, AddressDetails, SiteContacts } from './models/JobInterfaces';
import JobDetailsAddContactModalData from './JobDetailsAddContactModal';

interface NewJobDetailProps {
    systemCompanyId: number;
    jobDetails: JobDetailsNew;
    customers: CustomerList[];
    customerContacts: CustomerContactList[];
    siteContacts: SiteContacts;
    yesNo: GenericList[];
    update(fieldName: string, fieldValue: string, resetItems: boolean): void;
    updateNumber(fieldName: string, fieldValue: number, resetItems: boolean): void;
    updateChecked(fieldName: string, fieldValue: boolean, resetItems: boolean): void;
    updateCustomer(customerId: number): void;
    //addressType => 1 = Site Addr, 2 = Addr nearest Site Addr, 3 = Customer Addr
    updateAddress(addressType: number, updatedAddress: AddressDetails): void;
    updateSiteContacts(fieldName: string, fieldValue: string): void;
    clearSiteAddress(addrExists: boolean): void;
    clearCustomerAddress(custExists: boolean): void;
    updateCustomerContacts(updatedContacts: CustomerContactList[]): void;
    save(): void;
}

const NewJobDetailData = (props: NewJobDetailProps) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [showAddContact, setShowAddContact] = React.useState(false);
    const [addContact, setAddContact] = React.useState<AddContact>({
        customerId: -1,
        customerContactId: -1
    });
    const [useCustomerContact, setUseCustomerContact] = React.useState(false);
        
    const setAddress = (updatedAddress: AddressDetails) => {
        props.updateAddress(1, updatedAddress);
    }

    const setNearestAddress = (updatedAddress: AddressDetails) => {
        props.updateAddress(2, updatedAddress);
    }

    const setCustomerAddress = (updatedAddress: AddressDetails) => {
        props.updateAddress(3, updatedAddress);
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleChange = (e: any) => {
        clearErrors(e.target.name);
        props.update(e.target.name, e.target.value, false);
    }

    const handleCheckboxChange = (e: any) => {
        clearErrors(e.target.name);
        props.updateChecked(e.target.name, e.target.checked, false);
    }

    const handleSiteContactChange = (e: any) => {
        clearErrors(e.target.name);
        props.updateSiteContacts(e.target.name, e.target.value);
    }

    const handleUseCustomerContactChange = (e: any) => {
        setUseCustomerContact(e.target.checked);
        if (e.target.checked) {
            var siteContactName = props.jobDetails.customerContactFirstName + " " + props.jobDetails.customerContactLastName;
            var siteContactPhone = props.jobDetails.customerContactPhone;
            props.updateSiteContacts("siteContactName", siteContactName);
            props.updateSiteContacts("siteContactPhone", siteContactPhone);
        }
    }

    const handleCustomerContactChange = (e: any) => {
        e.preventDefault();
        clearErrors(e.target.name);

        var customerContactId = e.target.value * 1;
        props.updateNumber("customerContactId", customerContactId, false);

        if (customerContactId > 0) {
            //check to see if there is an email address, and if not then show edit contact box so user can add email
            var contact = props.customerContacts.find(cc => cc.id == customerContactId);
            if (contact) {
                var customerContactFirstName = contact.firstName;
                var customerContactLastName = contact.lastName;
                var customerContactPhone = contact.mobile == "" ? contact.phone : contact.mobile;
                var customerContactEmail = contact.email;
                props.update("customerContactFirstName", customerContactFirstName, false);
                props.update("customerContactLastName", customerContactLastName, false);
                props.update("customerContactPhone", customerContactPhone, false);
                props.update("customerContactEmail", customerContactEmail, false);
            }
        }
    }

    const handleAddressExistsChange = (e: any) => {
        props.clearSiteAddress(e.target.checked);
    }

    const handleCustomerExistsChange = (e: any) => {
        props.clearCustomerAddress(e.target.checked);
    }

    const customerChanged = (e: any) => {
        clearErrors("customerId");
        var customerId = e.target.value * 1;
        props.updateCustomer(customerId);
    }

    const hideAddContactModal = () => {
        setAddContact({ customerId: -1, customerContactId: -1 });
        setShowAddContact(false);
    }

    const showAddContactModal = (e: any) => {
        e.preventDefault();
        //show popup to add contact
        if (props.jobDetails.customerId && props.jobDetails.customerId > 0) {
            setAddContact({ customerId: props.jobDetails.customerId, customerContactId: -1 });
            setShowAddContact(true);
        } else {
            toast.error("Please select a Customer before adding a Contact");
        }
    }

    const showEditContactModal = (e: any) => {
        e.preventDefault();
        //show popup to edit contact
        if (props.jobDetails.customerId && props.jobDetails.customerId > 0) {
            editContactModal();
        } else {
            toast.error("Please select a Customer before editing a Contact");
        }
    }

    const editContactModal = () => {
        var customerId = (props.jobDetails.customerId ?? -1) * 1;
        var contactId = (props.jobDetails.customerContactId ?? -1) * 1;
        setAddContact({ customerId: customerId, customerContactId: contactId });
        setShowAddContact(true);
    }

    const saveAddContact = (contactDetails: CustomerContactList) => {
        props.updateNumber("customerContactId", addContact.customerContactId, false);
        var custContacts = [...props.customerContacts];

        if (addContact.customerContactId > 0) {
            //update customer details
            var existContact = custContacts.find(cc => cc.id == addContact.customerContactId);
            if (existContact) {
                existContact.name = contactDetails.name;
            }
        } else {
            //add the new contact details to the list
            custContacts.push(contactDetails);
            //when we add a new contact then auto update email address as they had to enter it when creating the contact
            props.update("customerContactEmail", contactDetails.email, false);
        }

        //reset add contact
        setAddContact({ customerId: -1, customerContactId: -1 });
        setShowAddContact(false);
        props.updateCustomerContacts(custContacts);
    }

    const validate = () => {
        let fields = props.jobDetails;
        let errorList: any = {};
        let formIsValid = true;

        if (fields.id === 0 && fields.priceOnly) {
            //user is just getting a price for the customer
            return true;
        }

        if (fields.customerExists || fields.id > 0) {
            if (!fields.customerId || fields.customerId < 1) {
                formIsValid = false;
                errorList["customerId"] = "Customer is required";
            }

            if (fields.customerCashSaleAccount) {
                //cash sales accounts use customer name, phone and email
                var contactName = (fields.customerContactFirstName + fields.customerContactLastName).trim();
                if (!contactName) {
                    formIsValid = false;
                    errorList["customerContact"] = "Customer Contact First/Last Name is required";
                }
                if (!fields.customerContactPhone) {
                    formIsValid = false;
                    errorList["customerContactPhone"] = "Customer Phone is required";
                }
                if (!fields.customerContactEmail) {
                    formIsValid = false;
                    errorList["customerContactEmail"] = "Customer Email is required";
                } else if (!fields.customerContactEmail.toLowerCase().includes("@")) {
                    formIsValid = false;
                    errorList["customerContactEmail"] = "Valid Customer Contact Email is required";
                }
            } else {
                if (!fields.customerContactId || fields.customerContactId < 1) {
                    formIsValid = false;
                    errorList["customerContactId"] = "Customer Contact is required";
                } else {
                    //need to make sure there is an email address for the contact
                    if (!fields.customerContactEmail) {
                        formIsValid = false;
                        errorList["customerContactConfirmEmail"] = "Customer Contact Email is required";
                    } else if (!fields.customerContactEmail.toLowerCase().includes("@")) {
                        formIsValid = false;
                        errorList["customerContactConfirmEmail"] = "Valid Customer Contact Email is required";
                    }
                }
            }
        } else {
            //adding a new quoting customer so test company name, customer name, phone and email
            if (!fields.customerName) {
                formIsValid = false;
                errorList["customer"] = "Customer Company Name is required";
            }
            if (!fields.customerContactFirstName || !fields.customerContactLastName) {
                formIsValid = false;
                errorList["customerContact"] = "Customer Contact First/Last Name is required";
            }
            if (!fields.customerAddress || !fields.customerAddress) {
                formIsValid = false;
                errorList["customerAddress"] = "Customer Address is required";
            }
            if (!fields.customerContactPhone) {
                formIsValid = false;
                errors["customerContactPhone"] = "Customer Phone is required";
            }
            if (!fields.customerContactEmail) {
                formIsValid = false;
                errorList["customerContactEmail"] = "Customer Email is required";
            } else if (!fields.customerContactEmail.toLowerCase().includes("@")) {
                formIsValid = false;
                errorList["customerContactEmail"] = "Valid Customer Contact Email is required";
            }
        }
  
        if (fields.siteAddressExists && !fields.siteAddress) {
            formIsValid = false;
            errorList["siteAddress"] = "Site Address is required";
        } else if (!fields.siteAddressExists) {
            if (!fields.manualStreetName) {
                formIsValid = false;
                errorList["manualStreetName"] = "Manual Street Name is required";
            }
            if (!fields.manualSuburb) {
                formIsValid = false;
                errorList["manualSuburb"] = "Manual Suburb is required";
            }
            if (!fields.manualState) {
                formIsValid = false;
                errorList["manualState"] = "Manual State is required";
            }
        }

        if (fields.isEvent || fields.isThreeDayHire) {
            if (!fields.eventName || fields.eventName === "") {
                formIsValid = false;
                var fieldName = fields.isEvent ? "Event " : "Invoicee ";
                errorList["eventName"] = fieldName + "Name is required";
            }

            //Add validation back in when we switch on extra event info
            //if (!fields.eventStartDate || fields.eventStartDate === "") {
            //    formIsValid = false;
            //    errors["eventStartDate"] = "Event Start Date is required";
            //} else if (!DateHelpers.isValidDate(fields.eventStartDate)) {
            //    formIsValid = false;
            //    errors["eventStartDate"] = "Valid Event Start Date is required";
            //}

            //if (!fields.eventEndDate || fields.eventEndDate === "") {
            //    formIsValid = false;
            //    errors["eventEndDate"] = "Event End Date is required";
            //} else if (!DateHelpers.isValidDate(fields.eventEndDate)) {
            //    formIsValid = false;
            //    errors["eventEndDate"] = "Valid Event End Date is required";
            //} else if (!DateHelpers.compareDateGreater(fields.eventStartDate, fields.eventEndDate)) {
            //    formIsValid = false;
            //    errors["eventEndDate"] = "Event End Date > Event Start Date";
            //}

            //if (!fields.deliveryWindowStart || fields.deliveryWindowStart === "") {
            //    formIsValid = false;
            //    errors["deliveryWindowStart"] = "Delivery Window Start is required";
            //} else if (!DateHelpers.isValidDate(fields.deliveryWindowStart)) {
            //    formIsValid = false;
            //    errors["deliveryWindowStart"] = "Valid Delivery Window Start is required";
            //}

            //if (!fields.deliveryWindowEnd || fields.deliveryWindowEnd === "") {
            //    formIsValid = false;
            //    errors["deliveryWindowEnd"] = "Delivery Window End is required";
            //} else if (!DateHelpers.isValidDate(fields.deliveryWindowEnd)) {
            //    formIsValid = false;
            //    errors["deliveryWindowEnd"] = "Valid Delivery Window End is required";
            //} else if (!DateHelpers.compareDateGreater(fields.deliveryWindowStart, fields.deliveryWindowEnd)) {
            //    formIsValid = false;
            //    errors["deliveryWindowEnd"] = "Delivery Window End > Delivery Window Start";
            //}
        }

        if (fields.jobType <= 0) {
            formIsValid = false;
            errorList["jobType"] = "Job Type is required";
        }

        if (fields.jobType === 2 && fields.id === 0) {
            //Need to enter Site Contact details if creating a new Pricelist job
            if (!props.siteContacts.siteContactName) {
                formIsValid = false;
                errorList["siteContactName"] = "Site Contact is required";
            }

            if (!props.siteContacts.siteContactPhone) {
                formIsValid = false;
                errorList["siteContactPhone"] = "Site Contact Phone is required";
            }
        }

        if (props.systemCompanyId === 2) {
            if (fields.truckAccess < 0) {
                formIsValid = false;
                errorList["truckAccess"] = "Truck Access is required";
            }

            if (fields.parking < 0) {
                formIsValid = false;
                errorList["parking"] = "Parking Available is required";
            }

            if (fields.siteClean < 0) {
                formIsValid = false;
                errorList["siteClean"] = "Is Site Clean is required";
            }
        } else if (props.systemCompanyId === 3) {
            if (fields.weekdayAccess < 0) {
                formIsValid = false;
                errorList["weekdayAccess"] = "Weekday Access is required";
            }

            if (fields.weekendAccess < 0) {
                formIsValid = false;
                errorList["weekendAccess"] = "Weekend Access is required";
            }

            if (fields.phoneCallRequired < 0) {
                formIsValid = false;
                errorList["phoneCallRequired"] = "Phone Call is required";
            }
        }

        if (fields.powerlinesWithinRange < 0) {
            formIsValid = false;
            errorList["powerlinesWithinRange"] = "Powerlines within 4.6m is required";
        }

        if (!fields.siteAddressExists && fields.addressSiteAddressDuration === 0) {
            formIsValid = false;
            errorList["addressSiteAddressDuration"] = "Mins to Site Address required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const saveGoNext = (e: any) => {
        e.preventDefault();
        if (validate()) {
            goNext();
        } else {
            toast.error("Check and fix errors before saving")
        }
    }

    const goNext = () => {
        props.save();
    }

    var googleContents = <GooglePlacesDropdown addressId="autocomplete" addressLabel={"Site Address"} placeholder={props.jobDetails.siteAddress} className="input-group" useLargeLabel={false} afterValid={setAddress} />
    var googleAddressNearSite = <GooglePlacesDropdown addressId="autocompletenearsite" addressLabel={"Address Near Site"} placeholder={props.jobDetails.addressNearSiteAddress} className="input-group" useLargeLabel={false} afterValid={setNearestAddress} />
    var customerAddress = <GooglePlacesDropdown addressId="autocompletecustomer" addressLabel={"Customer Address"} placeholder={props.jobDetails.customerAddress} className="input-group" useLargeLabel={false} afterValid={setCustomerAddress} />
    var addEditContact = <JobDetailsAddContactModalData showModal={showAddContact} hideModal={hideAddContactModal} customerId={addContact.customerId} id={addContact.customerContactId} saveModal={saveAddContact} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {addEditContact}
            </div>
                
            <div className="columngroup">
                <div className="columnleft">
                    <div className={props.systemCompanyId === 3 ? "" : "hidden"}>
                        <label className="input-group" htmlFor="priceOnly">
                            <span className="label">Get Price ONLY?<i className="fa-regular fa-circle-question edit--icon marginLeft10" title="Allows you to get a price for the customer without creating a job."></i></span>
                            <input className="checkbox" type="checkbox" id="priceOnly" name="priceOnly" checked={props.jobDetails.priceOnly} onChange={(e) => handleCheckboxChange(e)}></input>
                        </label>
                        <hr></hr>
                        <label className={props.jobDetails.priceOnly ? "input-group" : "hidden"} htmlFor="useTyabbPricelist">
                            <span className="label">Use RAB/PTH Pricelist?</span>
                            <input className="checkbox" type="checkbox" id="useTyabbPricelist" name="useTyabbPricelist" checked={props.jobDetails.useTyabbPricelist} onChange={(e) => handleCheckboxChange(e)}></input>
                        </label>
                    </div>
                    <div className={props.jobDetails.priceOnly ? "hidden" : ""}>
                        <div>
                            <label className={props.systemCompanyId === 3 ? "input-group--inline" : "hidden"} htmlFor="isEvent">
                                <span className="label">Is job for an Event?</span>
                                <input className="checkbox" type="checkbox" id="isEvent" name="isEvent" checked={props.jobDetails.isEvent} onChange={(e) => handleCheckboxChange(e)}></input>
                            </label>

                            <label className={props.systemCompanyId === 3 ? "input-group--inline" : "hidden"} htmlFor="isThreeDayHire">
                                <span className="label">Or Short-Term Hire?</span>
                                <input className="checkbox" type="checkbox" id="isThreeDayHire" name="isThreeDayHire" checked={props.jobDetails.isThreeDayHire} onChange={(e) => handleCheckboxChange(e)}></input>
                            </label>
                        </div>
                        <div className={props.jobDetails.isEvent || props.jobDetails.isThreeDayHire ? "" : "hidden"}>
                            <label className="input-group" htmlFor="eventName">
                                <span className="label">{props.jobDetails.isEvent ? "Event Name" : props.jobDetails.isThreeDayHire ? "Invoicee" : ""}</span>
                                <input className="input" type="text" id="eventName" name="eventName" value={props.jobDetails.eventName} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className={errors["eventName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventName"]}</span>
                        </div>
                        {/*<div className="hidden">*/}
                        {/*    <label className="input-group" htmlFor="eventStartDate">*/}
                        {/*        <span className="label">Event Start</span>*/}
                        {/*        <input className="input" type="datetime-local" id="eventStartDate" name="eventStartDate" value={props.jobDetails.eventStartDate} onChange={(e) => handleChange(e)}></input>*/}
                        {/*    </label>*/}
                        {/*    <span className={errors["eventStartDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventStartDate"]}</span>*/}
                        {/*    <label className="input-group" htmlFor="eventEndDate">*/}
                        {/*        <span className="label">Event End</span>*/}
                        {/*        <input className="input" type="datetime-local" id="eventEndDate" name="eventEndDate" value={props.jobDetails.eventEndDate} onChange={(e) => handleChange(e)}></input>*/}
                        {/*    </label>*/}
                        {/*    <span className={errors["eventEndDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["eventEndDate"]}</span>*/}
                        {/*    <div className="marginLeftLabel">Enter the earliest date and time that the product/s can be delivered.</div>*/}
                        {/*    <label className="input-group" htmlFor="deliveryWindowStart">*/}
                        {/*        <span className="label">Delivery Window Start</span>*/}
                        {/*        <input className="input" type="datetime-local" id="deliveryWindowStart" name="deliveryWindowStart" value={props.jobDetails.deliveryWindowStart} onChange={(e) => handleChange(e)}></input>*/}
                        {/*    </label>*/}
                        {/*    <span className={errors["deliveryWindowStart"] ? "label errors marginLeftLabel" : "hidden"}>{errors["deliveryWindowStart"]}</span>*/}
                        {/*    <div className="marginLeftLabel">Enter the latest date and time that the product/s can be delivered.</div>*/}
                        {/*    <label className="input-group" htmlFor="deliveryWindowEnd">*/}
                        {/*        <span className="label">Delivery Window End</span>*/}
                        {/*        <input className="input" type="datetime-local" id="deliveryWindowEnd" name="deliveryWindowEnd" value={props.jobDetails.deliveryWindowEnd} onChange={(e) => handleChange(e)}></input>*/}
                        {/*    </label>*/}
                        {/*    <span className={errors["deliveryWindowEnd"] ? "label errors marginLeftLabel" : "hidden"}>{errors["deliveryWindowEnd"]}</span>*/}
                        {/*    <hr></hr>*/}
                        {/*</div>*/}
                        <label htmlFor="customerExists">
                            <span className="label label--setsizesm">Customer Doesn't Exist</span>
                            <input className="checkbox" type="checkbox" id="customerExists" name="customerExists" checked={!props.jobDetails.customerExists} onChange={(e) => handleCustomerExistsChange(e)}></input>
                        </label>
                    </div>
                    <div className={props.jobDetails.id === 0 && !props.jobDetails.customerExists ? "hidden" : ""}>
                        <label className="input-group" htmlFor="customer">
                            <span className="label">Customer<span className={props.jobDetails.priceOnly ? "makeitred marginLeft10" : "hidden"}>OPTIONAL</span></span>
                            <select className="select marginRight10" id="customer" name="customerId" value={props.jobDetails.customerId} onChange={customerChanged}>
                                <option hidden defaultValue="-1"></option>
                                {props.customers.map(cust =>
                                    <option key={cust.id} value={cust.id}>{cust.name}</option>
                                )};
                            </select>
                            <a className={props.jobDetails.customerId && props.jobDetails.customerId > 0 ? "marginRight10" : "hidden"} href={"/customer/" + props.jobDetails.customerId} target="_blank">View</a>
                        </label>
                        <span className={props.jobDetails.accountLimitFormatted === "" ? "hidden" : "marginLeftLabel"}>{props.jobDetails.accountTypeId === 1 ? props.jobDetails.accountType : props.jobDetails.accountType + ' - ' + props.jobDetails.accountLimitFormatted}</span>
                        <span className={props.jobDetails.locked ? "makeitred makeitbold marginLeft20" : "hidden"}>Customer has been LOCKED by Accounts!</span>
                        <div className={!props.jobDetails.linkedToReckon && props.jobDetails.jobType === 2 ? "makeitred makeitbold marginLeftLabel" : "hidden"}>Let Accounts know that the customer needs to be linked to Reckon.</div>
                        <span className={errors["customerId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerId"]}</span>
                    </div>
                    <div className={props.jobDetails.customerCashSaleAccount || !props.jobDetails.customerExists || props.jobDetails.priceOnly ? "hidden" : ""}>
                        <label className="input-group" htmlFor="contact">
                            <span className="label">Contact</span>
                            <select className="select marginRight10" id="contact" name="customerContactId" value={props.jobDetails.customerContactId} onChange={(e) => handleCustomerContactChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {props.customerContacts.map(custcon =>
                                    <option key={custcon.id} value={custcon.id}>{custcon.name}</option>
                                )};
                            </select>
                            <a className={props.jobDetails.customerContactId && props.jobDetails.customerContactId > 0 ? "marginRight10" : "hidden"} href="#" onClick={(e) => showEditContactModal(e)}>Edit</a>
                            <a className={props.jobDetails.customerId && props.jobDetails.customerId > 0 ? "" : "hidden"} href="#" onClick={(e) => showAddContactModal(e)}>Add New</a>
                        </label>
                        <span className={errors["customerContactId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactId"]}</span>
                    </div>
                    <div className={!props.jobDetails.customerExists || props.jobDetails.customerCashSaleAccount ? "" : "hidden"}>
                        <div className={!props.jobDetails.customerExists ? "" : "hidden"}>
                            <hr></hr>
                            <label className="input-group" htmlFor="customer">
                                <span className="label">Customer Company Name</span>
                                <input className="input" type="text" maxLength={500} id="customerName" name="customerName" value={props.jobDetails.customerName} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className={errors["customer"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customer"]}</span>
                            {customerAddress}
                            <span className={errors["customerAddress"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerAddress"]}</span>
                            <label className="input-group" htmlFor="customerAbn">
                                <span className="label">Customer ABN (optional)</span>
                                <input className="input" type="text" maxLength={20} id="customerAbn" name="customerAbn" value={props.jobDetails.customerAbn} onChange={(e) => handleChange(e)}></input>
                            </label>
                        </div>
                        <label className="input-group" htmlFor="customerContactName">
                            <span className="label">Customer First/Last Name</span>
                            <div className="input-group--inline">
                                <input className="input marginRight10" type="text" maxLength={25} id="customerContactFirstName" name="customerContactFirstName" value={props.jobDetails.customerContactFirstName} onChange={(e) => handleChange(e)}></input>
                                <input className="input" type="text" maxLength={25} id="customerContactLastName" name="customerContactLastName" value={props.jobDetails.customerContactLastName} onChange={(e) => handleChange(e)}></input>
                            </div>
                        </label>
                        <span className={errors["customerContact"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContact"]}</span>
                        <label className="input-group" htmlFor="customerContactPhone">
                            <span className="label">Customer Phone</span>
                            <input className="input" type="text" maxLength={30} id="customerContactPhone" name="customerContactPhone" value={props.jobDetails.customerContactPhone} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["customerContactPhone"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactPhone"]}</span>
                        <label className="input-group" htmlFor="customerContactEmail">
                            <span className="label">Customer Email</span>
                            <input className="input" type="text" maxLength={200} id="customerContactEmail" name="customerContactEmail" value={props.jobDetails.customerContactEmail} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["customerContactEmail"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactEmail"]}</span>
                        <label className={props.systemCompanyId === 3 && !props.jobDetails.customerExists ? "input-group" : "hidden"} htmlFor="useTyabbPricelist">
                            <span className="label">Use RAB/PTH Pricelist?</span>
                            <input className="checkbox" type="checkbox" id="useTyabbPricelist" name="useTyabbPricelist" checked={props.jobDetails.useTyabbPricelist} onChange={(e) => handleCheckboxChange(e)}></input>
                        </label>
                        <hr></hr>
                    </div>
                    <div className={props.jobDetails.priceOnly ? "hidden" : ""}>
                        <label className={props.jobDetails.jobType === 2 || props.jobDetails.jobType === 3 ? "input-group" : "hidden"} htmlFor="sendEmailToCustomer">
                            <span className="label label--setsizesm">Send Confirmation Email</span>
                            <input className="checkbox" type="checkbox" id="sendEmailToCustomer" name="sendEmailToCustomer" checked={props.jobDetails.sendEmailToCustomer} onChange={(e) => handleCheckboxChange(e)} disabled={!props.jobDetails.canSendEmailToCustomer}></input>
                        </label>

                        <label className={props.jobDetails.customerContactId && props.jobDetails.customerContactId > 0 ? "input-group" : "hidden"} htmlFor="customerContactEmail">
                            <span className="label">Customer Email</span>
                            <input className="input marginRight10" type="text" maxLength={200} id="customerContactEmail" name="customerContactEmail" value={props.jobDetails.customerContactEmail} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["customerContactConfirmEmail"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactConfirmEmail"]}</span>

                        <div className={props.jobDetails.jobType === 2 ? "" : "hidden"}>
                            <div>
                                <label className="input-group" htmlFor="useCustomerContact">
                                    <span className="label">Use Customer Contact</span>
                                    <input className="checkbox" type="checkbox" name="useCustomerContact" checked={useCustomerContact} onChange={(e) => handleUseCustomerContactChange(e)}></input>
                                </label>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="siteContactName">
                                    <span className="label">Site Contact Name</span>
                                    <input className="input" type="text" maxLength={50} id="siteContactName" name="siteContactName" value={props.siteContacts.siteContactName} onChange={(e) => handleSiteContactChange(e)}></input>
                                </label>
                                <span className={errors["siteContactName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteContactName"]}</span>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="siteContactPhone">
                                    <span className="label">Site Contact Phone</span>
                                    <input className="input" type="text" maxLength={50} id="siteContactPhone" name="siteContactPhone" value={props.siteContacts.siteContactPhone} onChange={(e) => handleSiteContactChange(e)}></input>
                                </label>
                                <span className={errors["siteContactPhone"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteContactPhone"]}</span>
                            </div>
                        </div>
                        <div className={props.jobDetails.jobType === 2 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="siteContact2">
                                <span className="label">Site Contact 2</span>
                                <input className="input" type="text" maxLength={100} id="siteContact2" name="siteContact2" value={props.siteContacts.siteContact2} onChange={(e) => handleSiteContactChange(e)}></input>
                            </label>
                        </div>

                        <div className={!props.jobDetails.siteAddressExists ? "hidden" : ""}>
                            {googleContents}
                            <span className={errors["siteAddress"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteAddress"]}</span>
                            <div className="marginLeftLabel">Distance: {props.jobDetails.siteAddressDistance} Duration: {props.jobDetails.siteAddressDuration}</div>
                        </div>
                        <div>
                            <label className="input-group" htmlFor="siteAddrExists">
                                <span className="label label--setsizesm">Site Address Doesn't Exist</span>
                                <input className="checkbox" type="checkbox" id="siteAddrExists" name="siteAddressExists" checked={!props.jobDetails.siteAddressExists} onChange={(e) => handleAddressExistsChange(e)}></input>
                            </label>
                            <div className={props.jobDetails.siteAddressExists ? "hidden" : ""}>
                                <label className="input-group" htmlFor="manualStreetName">
                                    <span className="label">Site Street No/Name</span>
                                    <input className="input" type="text" id="manualStreetName" name="manualStreetName" value={props.jobDetails.manualStreetName} onChange={(e) => handleChange(e)}></input>
                                </label>
                                <span className={errors["manualStreetName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualStreetName"]}</span>

                                <label className="input-group" htmlFor="manualSuburb">
                                    <span className="label">Site Suburb</span>
                                    <input className="input" type="text" maxLength={40} id="manualSuburb" name="manualSuburb" value={props.jobDetails.manualSuburb} onChange={(e) => handleChange(e)}></input>
                                </label>
                                <span className={errors["manualSuburb"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualSuburb"]}</span>

                                <label className="input-group" htmlFor="manualPostCode">
                                    <span className="label">Site Post Code</span>
                                    <input className="input" type="text" id="manualPostCode" name="manualPostCode" value={props.jobDetails.manualPostCode} onChange={(e) => handleChange(e)}></input>
                                </label>
                                <span className={errors["manualPostCode"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualPostCode"]}</span>

                                <label className="input-group" htmlFor="manualState">
                                    <span className="label">State</span>
                                    <input className="input" type="text" id="manualState" name="manualState" value={props.jobDetails.manualState} onChange={(e) => handleChange(e)}></input>
                                </label>
                                <span className={errors["manualState"] ? "label errors marginLeftLabel" : "hidden"}>{errors["manualState"]}</span>
                            </div>
                        </div>
                        <div className={props.jobDetails.siteAddressExists ? "hidden" : ""}>
                            {googleAddressNearSite}
                            <span className="label errors marginLeftLabel">{errors["addressNearSiteAddress"]}</span>

                            <label className="input-group" htmlFor="addressSiteAddressDuration">
                                <span className="label">Mins to Site Address (one way)</span>
                                <input className="input" type="text" id="addressSiteAddressDuration" name="addressSiteAddressDuration" value={props.jobDetails.addressSiteAddressDuration} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className={errors["addressSiteAddressDuration"] ? "label errors marginLeftLabel" : "hidden"}>{errors["addressSiteAddressDuration"]}</span>
                        </div>
                        <div>
                            <label className="input-group" htmlFor="unitno">
                                <span className="label">Unit Number</span>
                                <input className="input" type="text" maxLength={10} id="unitno" name="unitNumber" value={props.jobDetails.unitNumber} onChange={(e) => handleChange(e)}></input>
                            </label>
                        </div>
                        <div>
                            <label className="input-group" htmlFor="lotno">
                                <span className="label">Lot Number</span>
                                <input className="input" type="text" id="lotno" name="lotNumber" value={props.jobDetails.lotNumber} onChange={(e) => handleChange(e)}></input>
                            </label>
                        </div>
                        <div className={props.systemCompanyId === 3 ? "" : "hidden"}>
                            <div className="marginLeftLabel">Enter any notes for delivery and/or placement of products for driver, <br></br> e.g. Shop Name, Access from [Street Name], Gate [Number] etc.</div>
                            <label className="input-group" htmlFor="deliveryPlacementNotes">
                                <span className="label">Delivery/Placement Notes</span>
                                <textarea className="input" rows={2} id="deliveryPlacementNotes" name="deliveryPlacementNotes" value={props.jobDetails.deliveryPlacementNotes} onChange={(e) => handleChange(e)}></textarea>
                            </label>
                        </div>
                        <div>
                            <label className="input-group" htmlFor="gatecode">
                                <span className="label">Gate Code</span>
                                <input className="input" type="text" maxLength={20} id="gatecode" name="gateCode" value={props.jobDetails.gateCode} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <label className="input-group" htmlFor="reference">
                                <span className="label">Reference</span>
                                <input className="input" type="text" maxLength={200} id="reference" name="reference" value={props.jobDetails.reference} onChange={(e) => handleChange(e)}></input>
                            </label>
                        </div>
                        <div className={props.systemCompanyId === 3 ? "hidden" : ""}>
                            <div>
                                <label className="input-group" htmlFor="truck">
                                    <span className="label">Truck Access?</span>
                                    <select className="select" name="truckAccess" value={props.jobDetails.truckAccess} onChange={(e) => handleChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {props.yesNo.map(yn =>
                                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={errors["truckAccess"] ? "label errors marginLeftLabel" : "hidden"}>{errors["truckAccess"]}</span>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="parking">
                                    <span className="label">Parking Avail?</span>
                                    <select className="select" id="parking" name="parking" value={props.jobDetails.parking} onChange={(e) => handleChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {props.yesNo.map(yn =>
                                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={errors["parking"] ? "label errors marginLeftLabel" : "hidden"}>{errors["parking"]}</span>
                            </div>
                            <div>
                                <label className="input-group" htmlFor="siteclean">
                                    <span className="label">Is Site {props.systemCompanyId === 2 ? "Clean" : "Level"}?</span>
                                    <select className="select" id="siteclean" name="siteClean" value={props.jobDetails.siteClean} onChange={(e) => handleChange(e)}>
                                        <option hidden defaultValue="-1"></option>
                                        {props.yesNo.map(yn =>
                                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                                        )};
                                    </select>
                                </label>
                                <span className={errors["siteClean"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteClean"]}</span>
                            </div>
                        </div>
                        <div>
                            <label className="input-group" htmlFor="powerlines">
                                <span className="label">Powerlines within 4.6m?</span>
                                <select className="select" id="powerlines" name="powerlinesWithinRange" value={props.jobDetails.powerlinesWithinRange} onChange={(e) => handleChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {props.yesNo.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={errors["powerlinesWithinRange"] ? "label errors marginLeftLabel" : "hidden"}>{errors["powerlinesWithinRange"]}</span>
                        </div>
                        <div className={props.systemCompanyId === 3 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="weekdayAccess">
                                <span className="label">Is there clear access between 7am and 3pm weekdays?</span>
                                <select className="select" id="weekdayAccess" name="weekdayAccess" value={props.jobDetails.weekdayAccess} onChange={(e) => handleChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {props.yesNo.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={errors["weekdayAccess"] ? "label errors marginLeftLabel" : "hidden"}>{errors["weekdayAccess"]}</span>

                            <label className="input-group" htmlFor="weekendAccess">
                                <span className="label">Is there weekend access?</span>
                                <select className="select" id="weekendAccess" name="weekendAccess" value={props.jobDetails.weekendAccess} onChange={(e) => handleChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {props.yesNo.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={errors["weekendAccess"] ? "label errors marginLeftLabel" : "hidden"}>{errors["weekendAccess"]}</span>

                            <label className="input-group" htmlFor="phoneCallRequired">
                                <span className="label">Phone call required prior to arrival?</span>
                                <select className="select" id="phoneCallRequired" name="phoneCallRequired" value={props.jobDetails.phoneCallRequired} onChange={(e) => handleChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {props.yesNo.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <span className={errors["phoneCallRequired"] ? "label errors marginLeftLabel" : "hidden"}>{errors["phoneCallRequired"]}</span>
                        </div>
                    </div>
                </div>
                <div className={props.jobDetails.priceOnly ? 'hidden' : 'columnright' }>
                    <div>
                        <label className="input-group--nowrap" htmlFor="notes">
                            <span className="label">Notes</span>
                            <textarea className="input notes__stepSize" id="notes" name="notes" value={props.jobDetails.notes} onChange={(e) => handleChange(e)}></textarea>
                        </label>
                    </div>
                    <div className={props.jobDetails.copyJobId > 0 && props.systemCompanyId === 2 ? "" : "hidden"}>
                        <label className="input-group" htmlFor="copyQuote">
                            <span className="label">Copy Quote</span>
                            <input className="input checkbox__Size" type="checkbox" id="copyQuote" name="copyQuote" checked={props.jobDetails.copyQuote} onChange={(e) => handleCheckboxChange(e)}></input>
                        </label>
                    </div>
                    <div className={props.jobDetails.copyJobId > 0 ? "" : "hidden"}>
                        <label className="input-group" htmlFor="linkJob">
                            <span className="label">Link Job {props.jobDetails.copyJobId}</span>
                            <input className="input checkbox__Size" type="checkbox" id="linkJob" name="linkJob" checked={props.jobDetails.linkJob} onChange={(e) => handleCheckboxChange(e)}></input>
                        </label>
                    </div>
                </div>
            </div>
            <div className="defaultbutton__container--right marginBottom10">
                <button className="defaultbutton defaultbutton--save marginLeft10" type="button" onClick={saveGoNext}>Next</button>
            </div>
        </div>
    );

    return renderDetails;
}

export default NewJobDetailData;
